import { useEffect, useMemo, useState } from 'react'

import { Theme } from '@mui/material/styles'
import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'
import useMediaQuery from '@mui/material/useMediaQuery'

import { Swiper, SwiperSlide } from 'swiper/react'
import SwiperCore, { Navigation, Mousewheel } from 'swiper'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/mousewheel'

import Product from 'src/components/ProductPreview/Product'
import config from 'src/config'
import {
  isCrossSellSections,
  isProduct,
  tCrossSellMeta,
  tCrossSellSection,
  tProduct,
  tProductPreview,
} from 'src/shared/frontendTypes/product'
import CrossSellProductModal from './CrossSellProductModal'
import { post } from 'src/functions/fetch'

SwiperCore.use([Navigation, Mousewheel])

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      marginTop: theme.spacing(6),
      paddingTop: theme.spacing(4),
      borderTop: '1px solid rgba(15, 23, 42, 0.08)',
    },

    header: {
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
      gap: theme.spacing(1.25),
      marginBottom: theme.spacing(1.5),
      [theme.breakpoints.down('sm')]: {
        overflowX: 'auto',
        paddingBottom: theme.spacing(.5),
      },
    },

    sliderMeta: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: theme.spacing(1),
      marginBottom: theme.spacing(1.25),
      color: '#64748b',
      fontSize: '.92rem',
      [theme.breakpoints.down('sm')]: {
        fontSize: '.86rem',
      },
    },

    tabs: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(1),
    },

    tabButton: {
      border: '1px solid rgba(15, 23, 42, 0.12)',
      background: '#fff',
      color: '#1f2937',
      borderRadius: 999,
      padding: '10px 16px',
      font: 'inherit',
      cursor: 'pointer',
      transition: 'all .18s ease',
      '&:hover': {
        borderColor: 'rgba(15, 23, 42, 0.28)',
      },
    },

    tabButtonActive: {
      background: '#111827',
      color: '#fff',
      borderColor: '#111827',
    },

    sliderRoot: {
      position: 'relative',
      overflow: 'hidden',
      padding: theme.spacing(.5, 0, .75),
      '&:before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        borderRadius: 32,
        background: 'linear-gradient(180deg, rgba(248, 250, 252, 0.92) 0%, rgba(255, 255, 255, 0) 100%)',
        pointerEvents: 'none',
      },
      '& .swiper': {
        overflow: 'hidden',
        paddingBottom: theme.spacing(1),
      },
      '& .swiper-wrapper': {
        alignItems: 'stretch',
      },
      '& .swiper-slide': {
        height: 'auto',
        display: 'flex',
      },
      '& .swiper-button-prev, & .swiper-button-next': {
        width: 42,
        height: 42,
        borderRadius: '50%',
        backgroundColor: 'rgba(255,255,255,.96)',
        color: '#1f2933',
        boxShadow: '0 10px 24px rgba(15, 23, 42, 0.12)',
        top: '42%',
        '&:after': {
          fontSize: 14,
          fontWeight: 700,
        },
        [theme.breakpoints.down('sm')]: {
          width: 34,
          height: 34,
          top: '40%',
          '&:after': {
            fontSize: 11,
          },
        },
      },
      '& .swiper-button-disabled': {
        opacity: .3,
      },
      '& .swiper-slide > section': {
        width: '100%',
        height: '100%',
      },
    },
  }),
)

type FrontendSectionId = 'passend' | 'alternativen' | 'serie'

type FrontendSection = {
  id: FrontendSectionId
  title: string
  products: tProductPreview[]
}

type SelectedAttributes = Record<string, number | ''>
type TechnicalFacet = keyof tCrossSellMeta
type CurrentCombinationSelection = {
  key?: string
  options?: number[]
} | null

const TECHNICAL_TAG_REGEX =
  /^cs-(serie|hersteller|farbe|teil)(?:-([0-9]+(?:-[0-9]+)*|base))?:(.+)$/i
const BASE_COMBINATION_KEY = '__base__'

const normalizeText = (value?: string) =>
  String(value || '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()

const getColorMatchValues = (value?: string) => {
  const display = String(value || '').replace(/\s+/g, ' ').trim()
  const values = [
    display,
    display.match(/\bfarbe\s*([0-9]{1,4})\b/i)?.[1],
    display.match(/\(([0-9]{1,4})\)/)?.[1],
    display.match(/^([0-9]{1,4})(?:\b|$)/)?.[1],
  ]

  return Array.from(new Set(values.map(normalizeText).filter(Boolean)))
}

const technicalValuesMatch = (
  facet: TechnicalFacet,
  currentValue?: string,
  previewValue?: string,
) => {
  if (facet !== 'farbe') {
    return normalizeText(currentValue) === normalizeText(previewValue)
  }

  const currentValues = getColorMatchValues(currentValue)
  const previewValues = new Set(getColorMatchValues(previewValue))

  return currentValues.some(value => previewValues.has(value))
}

const toUniqueNumbers = (value?: string | null) => {
  const numbers = String(value || '')
    .split('-')
    .map(entry => Number(entry))
    .filter((entry): entry is number => Number.isInteger(entry))

  return Array.from(new Set(numbers))
}

const normalizeCombinationKey = (value?: string | null) => {
  const display = String(value || '').trim()
  if (!display || display.toLowerCase() === 'base') return BASE_COMBINATION_KEY

  const normalizedNumbers = toUniqueNumbers(display)
  return normalizedNumbers.length > 0
    ? normalizedNumbers.join('-')
    : BASE_COMBINATION_KEY
}

const matchesCombinationKey = (
  selectedCombinationKey: string,
  tagCombinationKey: string,
) => {
  if (tagCombinationKey === BASE_COMBINATION_KEY) return false

  const selectedOptions = new Set(toUniqueNumbers(selectedCombinationKey))
  const tagOptions = toUniqueNumbers(tagCombinationKey)

  if (selectedOptions.size === 0 || tagOptions.length === 0) return false

  return tagOptions.every(optionId => selectedOptions.has(optionId))
}

const buildSelectedCombinationKey = (
  selectedAttributes: SelectedAttributes,
  combination?: CurrentCombinationSelection,
) => {
  if (combination?.options?.length) {
    return combination.options.join('-')
  }

  const optionIds = Object.values(selectedAttributes)
    .filter((value): value is number => typeof value === 'number')

  return optionIds.length > 0
    ? optionIds.join('-')
    : BASE_COMBINATION_KEY
}

const buildCurrentTechnicalMeta = (
  product: tProduct,
  technicalTags: string[] | undefined,
  selectedAttributes: SelectedAttributes,
  combination?: CurrentCombinationSelection,
): tCrossSellMeta => {
  const selectedCombinationKey = buildSelectedCombinationKey(selectedAttributes, combination)
  const baseMeta: Partial<Record<TechnicalFacet, string>> = {}
  const specificMeta: Partial<Record<TechnicalFacet, string>> = {}
  const specificMetaStrength: Partial<Record<TechnicalFacet, number>> = {}
  const colorAttribute = product.attributes?.find(attribute => /farbe|color/i.test(attribute.name))

  for (const rawTag of technicalTags || []) {
    const match = normalizeText(rawTag).match(TECHNICAL_TAG_REGEX)
    if (!match?.[1] || !match?.[3]) continue

    const facet = match[1].toLowerCase() as TechnicalFacet
    const value = String(match[3]).trim()
    const combinationKey = normalizeCombinationKey(match[2])

    if (combinationKey === BASE_COMBINATION_KEY) {
      baseMeta[facet] = value
    }

    if (matchesCombinationKey(selectedCombinationKey, combinationKey)) {
      const strength = toUniqueNumbers(combinationKey).length
      const currentStrength = specificMetaStrength[facet] || 0
      if (strength < currentStrength) continue

      specificMeta[facet] = value
      specificMetaStrength[facet] = strength
    }
  }

  const selectedColorOption = colorAttribute
    ?.options.find(option => option.id === selectedAttributes[colorAttribute.id])
    ?.name

  return {
    ...(baseMeta.hersteller || specificMeta.hersteller ? { hersteller: specificMeta.hersteller || baseMeta.hersteller } : {}),
    ...(baseMeta.serie || specificMeta.serie ? { serie: specificMeta.serie || baseMeta.serie } : {}),
    ...(selectedColorOption || baseMeta.farbe || specificMeta.farbe
      ? { farbe: selectedColorOption || specificMeta.farbe || baseMeta.farbe }
      : {}),
    ...(baseMeta.teil || specificMeta.teil ? { teil: specificMeta.teil || baseMeta.teil } : {}),
  }
}

const isSameProductSelection = (
  currentProduct: tProduct,
  currentCombination: CurrentCombinationSelection,
  selectedAttributes: SelectedAttributes,
  preview: tProductPreview,
) => {
  if (preview.id !== currentProduct.id) return false

  if (currentCombination?.key && preview.key) {
    return currentCombination.key === preview.key
  }

  const currentKey = buildSelectedCombinationKey(selectedAttributes, currentCombination)
  const previewKey = normalizeCombinationKey(
    (preview.options || []).length > 0
      ? (preview.options || []).join('-')
      : 'base',
  )

  return currentKey === previewKey
}

const matchesMeta = (
  previewMeta: tCrossSellMeta | undefined,
  currentMeta: tCrossSellMeta,
  facet: TechnicalFacet,
) => {
  const currentValue = normalizeText(currentMeta[facet])
  if (!currentValue) return true

  const previewValue = normalizeText(previewMeta?.[facet])
  if (!previewValue) return false

  return technicalValuesMatch(facet, currentMeta[facet], previewMeta?.[facet])
}

const inferPreviewColor = (
  preview: tProductPreview,
  currentProduct: tProduct,
) => {
  const colorAttribute = currentProduct.attributes?.find(attribute => /farbe|color/i.test(attribute.name))
  const selectedColorOption = colorAttribute
    ?.options.find(option => (preview.options || []).includes(option.id))
    ?.name
    ?.trim()

  if (selectedColorOption) return selectedColorOption

  const haystack = normalizeText([
    ...(preview.attributes || []),
    preview.metaTitle,
    preview.name,
  ].join(' '))

  for (const option of colorAttribute?.options || []) {
    const optionName = normalizeText(option.name)
    if (!optionName) continue
    if (haystack.includes(optionName)) return option.name.trim()
  }

  const colorLine = (preview.attributes || []).find(attribute => /^(farbe|color)\s*:/i.test(attribute))
  const attributeColor = colorLine?.split(':').slice(1).join(':').trim()
  if (attributeColor) return attributeColor

  return String(preview.crossSellMeta?.farbe || '').trim()
}

const inferPreviewMeta = (
  preview: tProductPreview,
  currentProduct: tProduct,
): tCrossSellMeta => {
  const previewText = normalizeText([
    preview.metaTitle,
    preview.name,
    ...(preview.attributes || []),
  ].join(' '))

  const explicitHersteller = String(preview.crossSellMeta?.hersteller || '').trim()
  const explicitSerie = String(preview.crossSellMeta?.serie || '').trim()
  const explicitTeil = String(preview.crossSellMeta?.teil || '').trim()
  const inferredColor = inferPreviewColor(preview, currentProduct)

  const currentSerie = String(
    currentProduct.crossSellTechnicalTags
      ?.map(tag => {
        const match = normalizeText(tag).match(TECHNICAL_TAG_REGEX)
        return match?.[1] === 'serie' ? String(match[3]).trim() : ''
      })
      .find(Boolean)
      || '',
  ).trim()

  const inferredSerie = explicitSerie
    || (currentSerie && previewText.includes(normalizeText(currentSerie)) ? currentSerie : '')

  return {
    ...(explicitHersteller || preview.brandTitel ? { hersteller: explicitHersteller || preview.brandTitel } : {}),
    ...(inferredSerie ? { serie: inferredSerie } : {}),
    ...(explicitTeil ? { teil: explicitTeil } : {}),
    ...(inferredColor ? { farbe: inferredColor } : {}),
  }
}

const decoratePreviewForSection = (
  preview: tProductPreview,
  sectionId: FrontendSectionId,
  currentProduct: tProduct,
) => {
  if (sectionId !== 'alternativen') return preview

  const color = inferPreviewColor(preview, currentProduct)
  if (!color) return preview

  if (normalizeText(preview.metaTitle).includes(normalizeText(color))) return preview

  return {
    ...preview,
    metaTitle: `${preview.metaTitle} - ${color}`,
  }
}

const filterSectionProducts = (
  sectionId: FrontendSectionId,
  products: tProductPreview[],
  currentProduct: tProduct,
  currentCombination: CurrentCombinationSelection,
  selectedAttributes: SelectedAttributes,
  currentMeta: tCrossSellMeta,
  trustServerResult = false,
) => dedupeProducts(products).filter(preview => {
  const previewMeta = inferPreviewMeta(preview, currentProduct)

  if (isSameProductSelection(currentProduct, currentCombination, selectedAttributes, preview)) {
    return false
  }

  if (sectionId === 'alternativen') {
    const matches =
      matchesMeta(previewMeta, currentMeta, 'hersteller')
      && matchesMeta(previewMeta, currentMeta, 'serie')
      && matchesMeta(previewMeta, currentMeta, 'teil')

    if (!matches) return false

    const currentColor = normalizeText(currentMeta.farbe)
    const previewColor = normalizeText(previewMeta.farbe)
    if (currentColor && previewColor && technicalValuesMatch('farbe', currentMeta.farbe, previewMeta.farbe)) {
      return false
    }
  }

  if (sectionId === 'serie') {
    if (trustServerResult) {
      const currentPart = normalizeText(currentMeta.teil)
      const previewPart = normalizeText(previewMeta.teil)

      if (currentPart && previewPart && currentPart === previewPart) {
        return false
      }

      return true
    }

    const matches =
      matchesMeta(previewMeta, currentMeta, 'hersteller')
      && matchesMeta(previewMeta, currentMeta, 'serie')
      && matchesMeta(previewMeta, currentMeta, 'farbe')

    if (!matches) return false

    const currentPart = normalizeText(currentMeta.teil)
    const previewPart = normalizeText(previewMeta.teil)
    if (currentPart && previewPart && currentPart === previewPart) {
      return false
    }
  }

  return true
})

const dedupeProducts = (products: tProductPreview[]) => {
  const bySignature = new Set<string>()
  const unique: tProductPreview[] = []

  for (const product of products) {
    const signature = [
      product.id,
      product.key || '',
      (product.options || []).join('-'),
    ].join(':')

    if (bySignature.has(signature)) continue
    bySignature.add(signature)
    unique.push(product)
  }

  return unique
}

const getSectionTitle = (
  sections: tCrossSellSection[],
  fallback: string,
) => sections.find(section => Boolean(section.titel?.trim()))?.titel?.trim() || fallback

const buildFrontendSections = (
  sections: tCrossSellSection[] = [],
  currentProduct: tProduct,
  currentCombination: CurrentCombinationSelection,
  selectedAttributes: SelectedAttributes,
  currentMeta: tCrossSellMeta,
  dynamicSerieSection?: tCrossSellSection | null,
  omitStaticSerie = false,
): FrontendSection[] => {
  const baseSections = omitStaticSerie
    ? sections.filter(section => section.art !== 'serie')
    : sections

  const matchingSections = baseSections.filter(section =>
    section.art === 'zubehoer' || section.art === 'ergaenzung',
  )
  const result: FrontendSection[] = []

  const passendProducts = filterSectionProducts(
    'passend',
    matchingSections.flatMap(section => section.products || []),
    currentProduct,
    currentCombination,
    selectedAttributes,
    currentMeta,
  )
  if (passendProducts.length > 0) {
    result.push({
      id: 'passend',
      title: getSectionTitle(matchingSections, 'Passend dazu'),
      products: passendProducts.map(product =>
        decoratePreviewForSection(product, 'passend', currentProduct),
      ),
    })
  }

  const alternativen = baseSections.find(section => section.art === 'alternativen')
  if ((alternativen?.products || []).length > 0) {
    const products = filterSectionProducts(
      'alternativen',
      alternativen?.products || [],
      currentProduct,
      currentCombination,
      selectedAttributes,
      currentMeta,
    )
    if (products.length === 0) {
      // keep looking at the next section
    } else {
      result.push({
        id: 'alternativen',
        title: alternativen?.titel?.trim() || 'Alternativen',
        products: products.map(product =>
          decoratePreviewForSection(product, 'alternativen', currentProduct),
        ),
      })
    }
  }

  const serie = dynamicSerieSection || baseSections.find(section => section.art === 'serie')
  if ((serie?.products || []).length > 0) {
    const products = filterSectionProducts(
      'serie',
      serie?.products || [],
      currentProduct,
      currentCombination,
      selectedAttributes,
      currentMeta,
      Boolean(dynamicSerieSection),
    )
    if (products.length === 0) {
      return result
    }

    result.push({
      id: 'serie',
      title: serie?.titel?.trim() || 'Auch aus dieser Serie',
      products: products.map(product =>
        decoratePreviewForSection(product, 'serie', currentProduct),
      ),
    })
  }

  return result
}

const CrossSellSections: React.FC<{
  product: tProduct
  selectedAttributes: SelectedAttributes
  combination?: CurrentCombinationSelection
}> = ({ product, selectedAttributes, combination }) => {
  const classes = useStyles()
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'))
  const productForCrossSell = product
  const hasStaticSerie = useMemo(
    () => Boolean(productForCrossSell.crossSellSections?.some(section => section.art === 'serie')),
    [productForCrossSell.crossSellSections],
  )
  const hasColorAttribute = useMemo(
    () => Boolean(productForCrossSell.attributes?.some(attribute => /farbe|color/i.test(attribute.name))),
    [productForCrossSell.attributes],
  )
  const shouldLoadDynamicSerie = hasStaticSerie && hasColorAttribute
  const selectedOptionIds = useMemo(() => {
    const optionIds = combination?.options?.length
      ? combination.options
      : Object.values(selectedAttributes).filter((value): value is number => typeof value === 'number')

    return Array.from(new Set(optionIds)).sort((a, b) => a - b)
  }, [combination?.options, selectedAttributes])
  const dynamicSerieRequestKey = useMemo(
    () => selectedOptionIds.join('-'),
    [selectedOptionIds],
  )

  const currentMeta = useMemo(
    () => buildCurrentTechnicalMeta(productForCrossSell, productForCrossSell.crossSellTechnicalTags, selectedAttributes, combination),
    [combination, productForCrossSell, productForCrossSell.crossSellTechnicalTags, selectedAttributes],
  )
  const [dynamicSerieSectionsByKey, setDynamicSerieSectionsByKey] = useState<Record<string, tCrossSellSection | null>>({})
  const dynamicSerieSection = shouldLoadDynamicSerie && dynamicSerieRequestKey
    ? dynamicSerieSectionsByKey[dynamicSerieRequestKey] || null
    : null
  const sections = useMemo(
    () => buildFrontendSections(
      productForCrossSell.crossSellSections,
      productForCrossSell,
      combination || null,
      selectedAttributes,
      currentMeta,
      dynamicSerieSection,
      shouldLoadDynamicSerie,
    ),
    [
      combination,
      currentMeta,
      dynamicSerieSection,
      productForCrossSell,
      productForCrossSell.crossSellSections,
      selectedAttributes,
      shouldLoadDynamicSerie,
    ],
  )
  const [activeSectionId, setActiveSectionId] = useState<FrontendSectionId | null>(
    sections[0]?.id || null,
  )
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0)
  const [modalPreview, setModalPreview] = useState<tProductPreview | null>(null)
  const [prefetchedProductsById, setPrefetchedProductsById] = useState<Record<number, tProduct>>({})

  useEffect(() => {
    if (!shouldLoadDynamicSerie) return
    if (!dynamicSerieRequestKey || !normalizeText(currentMeta.farbe)) return
    if (Object.prototype.hasOwnProperty.call(dynamicSerieSectionsByKey, dynamicSerieRequestKey)) return

    const controller = new AbortController()

    void post({
      url: `product/${productForCrossSell.id}/cross-sell`,
      body: {
        ...config.shop,
        selectedOptions: selectedOptionIds,
        arts: ['serie'],
      },
      signal: controller.signal,
    }).then(data => {
      if (controller.signal.aborted) return

      const nextSerieSection = isCrossSellSections(data)
        ? data.find(section => section.art === 'serie') || null
        : null

      setDynamicSerieSectionsByKey(prev => ({
        ...prev,
        [dynamicSerieRequestKey]: nextSerieSection,
      }))
    })

    return () => controller.abort()
  }, [
    currentMeta.farbe,
    dynamicSerieRequestKey,
    dynamicSerieSectionsByKey,
    productForCrossSell.id,
    selectedOptionIds,
    shouldLoadDynamicSerie,
  ])

  useEffect(() => {
    if (sections.length === 0) {
      setActiveSectionId(null)
      setCurrentSlideIndex(0)
      return
    }

    if (!activeSectionId || !sections.some(section => section.id === activeSectionId)) {
      setActiveSectionId(sections[0].id)
      setCurrentSlideIndex(0)
    }
  }, [activeSectionId, sections])

  useEffect(() => {
    setCurrentSlideIndex(0)
  }, [activeSectionId])

  useEffect(() => {
    const activeSection = sections.find(section => section.id === activeSectionId) || sections[0]
    if (!activeSection) return

    const idsToPrefetch = activeSection.products
      .map(entry => entry.id)
      .filter((id, index, list) => list.indexOf(id) === index)
      .filter(id => id !== product.id && !prefetchedProductsById[id])
      .slice(0, 4)

    if (idsToPrefetch.length === 0) return

    const controller = new AbortController()
    const timeout = window.setTimeout(() => {
      void Promise.all(idsToPrefetch.map(async id => {
        const data = await post({
          url: `product/${id}`,
          body: {
            ...config.shop,
            skipAlsoPurchased: true,
            skipCrossSellSections: true,
          },
          signal: controller.signal,
        })

        if (!isProduct(data) || controller.signal.aborted) return null
        return data
      })).then(products => {
        if (controller.signal.aborted) return

        const validProducts = products.filter((entry): entry is tProduct => Boolean(entry))
        if (validProducts.length === 0) return

        setPrefetchedProductsById(prev => ({
          ...prev,
          ...Object.fromEntries(validProducts.map(entry => [entry.id, entry])),
        }))
      })
    }, 250)

    return () => {
      controller.abort()
      window.clearTimeout(timeout)
    }
  }, [activeSectionId, prefetchedProductsById, product.id, sections])

  if (sections.length === 0 || !activeSectionId) return null

  const activeSection = sections.find(section => section.id === activeSectionId) || sections[0]
  const visibleProductsMobile = 2
  const visibleProductsCount = isMobile
    ? Math.min(visibleProductsMobile, activeSection.products.length)
    : 1
  const visibleStart = Math.min(activeSection.products.length, currentSlideIndex + 1)
  const visibleEnd = Math.min(activeSection.products.length, currentSlideIndex + visibleProductsCount)
  const visibleRange = visibleEnd > visibleStart
    ? `${visibleStart}-${visibleEnd}`
    : String(visibleStart)

  return (
    <section className={classes.root}>
      <div className={classes.header}>
        <div className={classes.tabs}>
          {sections.map(section => (
            <button
              key={section.id}
              type='button'
              className={[
                classes.tabButton,
                section.id === activeSection.id ? classes.tabButtonActive : '',
              ].join(' ')}
              onClick={() => setActiveSectionId(section.id)}
            >
              {section.title}
            </button>
          ))}
        </div>
      </div>

      <div className={classes.sliderMeta}>
        <span>
          {activeSection.products.length} Artikel
        </span>
        <span>
          {visibleRange} / {activeSection.products.length}
          {isMobile && activeSection.products.length > 1 ? ' • Wischen oder Pfeile nutzen' : ''}
        </span>
      </div>

      <div className={classes.sliderRoot}>
        <Swiper
          key={activeSection.id}
          navigation
          watchOverflow
          mousewheel={{ forceToAxis: true, releaseOnEdges: true }}
          speed={420}
          allowTouchMove
          simulateTouch
          grabCursor
          resistanceRatio={.2}
          longSwipesRatio={.18}
          longSwipesMs={180}
          onSlideChange={swiper => setCurrentSlideIndex(swiper.activeIndex)}
          slidesPerView={2}
          spaceBetween={12}
          breakpoints={{
            600: {
              slidesPerView: 2.45,
              spaceBetween: 16,
            },
            960: {
              slidesPerView: 3.45,
              spaceBetween: 18,
            },
            1280: {
              slidesPerView: 4.45,
              spaceBetween: 20,
            },
          }}
        >
          {activeSection.products.map(productPreview => (
            <SwiperSlide
              key={[
                activeSection.id,
                productPreview.id,
                productPreview.key || '',
                (productPreview.options || []).join('-'),
              ].join(':')}
            >
              <Product
                product={productPreview}
                variant='slider'
                display={['title', 'attributes', 'price', 'deliverytime']}
                showAvailability
                hoverEffect={false}
                imageSizePx={isMobile ? undefined : 148}
                emphasizeInStock
                onOpenProduct={setModalPreview}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      <CrossSellProductModal
        open={Boolean(modalPreview)}
        preview={modalPreview}
        currentProduct={productForCrossSell}
        prefetchedProduct={modalPreview ? prefetchedProductsById[modalPreview.id] : null}
        onClose={() => setModalPreview(null)}
      />
    </section>
  )
}

export default CrossSellSections
